/**
 * Token Store — armazenamento SEGURO do access token (spec §11.9)
 *
 * ARQUITETURA DE TOKEN (ADR — spec §11.9):
 *   - Access token: APENAS em memória (esta store). Nunca em localStorage/sessionStorage.
 *   - Refresh token: cookie httpOnly; Secure; SameSite=Strict servido pelo BFF.
 *   - O BFF (mesma origem) recebe o cookie automaticamente nas requisições.
 *
 * RATIONALE:
 *   - localStorage/sessionStorage são acessíveis via XSS → roubo de token persistente.
 *   - Memória: destruída ao fechar a aba → janela de ataque muito menor.
 *   - Cookie httpOnly: inacessível a JS → XSS não consegue ler.
 *
 * REFRESH SINGLE-FLIGHT:
 *   - Se múltiplos requests chegarem com token expirado simultaneamente,
 *     apenas ONE requisição de refresh é feita.
 *   - Os demais aguardam em fila (pending promises).
 *   - Evita thundering herd de refreshes.
 */

interface TokenPayload {
  sub: string
  email: string
  role: string
  exp: number
  jti: string
  // Perfis de aplicação (RBAC). No dev-login vêm em app_metadata.ppm_perfis; em
  // produção a fonte autoritativa é o banco (W1-54) — o front usa isto apenas
  // como dica de UX (disable-por-papel); o backend continua a fronteira real.
  app_metadata?: { ppm_perfis?: string[] }
  // Authenticator Assurance Level do Supabase: "aal1" (1FA) ou "aal2" (MFA
  // verificado nesta sessão). Usado pelo gate de MFA obrigatório no login
  // (routes/require-mfa.tsx) — decodificado client-side sem verificar
  // assinatura, então é só uma dica de UX; o backend valida o claim real
  // do JWT a cada requisição (backend/internal/auth/principal.go).
  aal?: string
}

// ─── Estado em memória (não serializável) ────────────────────────────────────

let _accessToken: string | null = null
let _tokenPayload: TokenPayload | null = null

// Single-flight refresh
let _refreshPromise: Promise<string | null> | null = null

// ─── Acesso ao token ──────────────────────────────────────────────────────────

export function getAccessToken(): string | null {
  return _accessToken
}

export function getTokenPayload(): TokenPayload | null {
  return _tokenPayload
}

export function setAccessToken(token: string): void {
  _accessToken = token
  _tokenPayload = parseJwt(token)
}

export function clearAccessToken(): void {
  _accessToken = null
  _tokenPayload = null
}

export function isTokenExpired(): boolean {
  if (!_tokenPayload) return true
  // Margem de 30s para renovar antes do vencimento
  return Date.now() / 1000 >= _tokenPayload.exp - 30
}

// ─── Refresh single-flight ────────────────────────────────────────────────────

/**
 * Solicita novo access token ao BFF.
 * O BFF usa o refresh token do cookie httpOnly para emitir um novo JWT.
 *
 * SINGLE-FLIGHT: chamadas concorrentes compartilham a mesma Promise.
 * Quando ela resolve, todos os chamadores recebem o resultado.
 */
export async function refreshAccessToken(
  bffRefreshEndpoint: string,
): Promise<string | null> {
  // Se já há um refresh em andamento, aguarda o mesmo
  if (_refreshPromise) {
    return _refreshPromise
  }

  _refreshPromise = _doRefresh(bffRefreshEndpoint)

  try {
    const token = await _refreshPromise
    return token
  } finally {
    // Limpa independente de sucesso/falha para permitir retry futuro
    _refreshPromise = null
  }
}

async function _doRefresh(bffRefreshEndpoint: string): Promise<string | null> {
  try {
    const response = await fetch(bffRefreshEndpoint, {
      method: 'POST',
      credentials: 'include', // envia o cookie httpOnly do refresh token
      headers: { 'Content-Type': 'application/json' },
    })

    if (!response.ok) {
      clearAccessToken()
      return null
    }

    const data = (await response.json()) as { access_token: string }
    setAccessToken(data.access_token)
    return data.access_token
  } catch {
    clearAccessToken()
    return null
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseJwt(token: string): TokenPayload | null {
  try {
    const base64Url = token.split('.')[1]
    if (!base64Url) return null
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    )
    return JSON.parse(jsonPayload) as TokenPayload
  } catch {
    return null
  }
}
